import { mapPeople } from '@/reports/person/utils/mapPersonProperties';
import {
  commonTypes,
  peopleTypes,
  reportTypes,
  serializedTypes,
} from '@/reports/types';
import { serializeProfiles, serializeJobs, sortProfiles } from '@/utils/data';
import { formatCountryPhone } from '@/utils/index';
import isBlacklisted from '@/utils/isBlacklisted';

function addressHasPropertyReport(address: commonTypes.Address) {
  const isNotPoBoxAddress = !address?.parsed?.street_name
    ?.toLowerCase()
    .includes('po box');
  const parentPropertyId = Boolean(
    address.property_related_info
      ? address.property_related_info.parent_property_id
      : null
  );

  return (
    isNotPoBoxAddress && (address.property_record_available || parentPropertyId)
  );
}

export function sortAddressesBySeenDate(
  addresses: commonTypes.Addresses
): commonTypes.Addresses {
  return addresses.sort((a, b) => {
    const getLastSeenDate = (address: commonTypes.Address) => {
      const dateString = address?.meta?.last_seen_date?.full;
      return dateString ? new Date(dateString) : null;
    };

    const getFirstSeenDate = (address: commonTypes.Address) => {
      const dateString = address?.meta?.first_seen_date?.full;
      return dateString ? new Date(dateString) : null;
    };

    const lastSeenDateA = getLastSeenDate(a);
    const lastSeenDateB = getLastSeenDate(b);

    if (lastSeenDateA && lastSeenDateB) {
      if (lastSeenDateA.getTime() !== lastSeenDateB.getTime()) {
        return lastSeenDateB.getTime() - lastSeenDateA.getTime();
      }

      const firstSeenDateA = getFirstSeenDate(a);
      const firstSeenDateB = getFirstSeenDate(b);

      if (firstSeenDateA && firstSeenDateB) {
        return firstSeenDateB.getTime() - firstSeenDateA.getTime();
      }

      if (firstSeenDateA) {
        return -1;
      }

      if (firstSeenDateB) {
        return 1;
      }
    }

    if (lastSeenDateA) return -1;
    if (lastSeenDateB) return 1;

    const firstSeenDateA = getFirstSeenDate(a);
    const firstSeenDateB = getFirstSeenDate(b);

    if (firstSeenDateA && firstSeenDateB) {
      return firstSeenDateB.getTime() - firstSeenDateA.getTime();
    }

    if (firstSeenDateA) return -1;
    if (firstSeenDateB) return 1;

    return 0;
  });
}

function serializeAddresses(
  addresses: commonTypes.Address[] | undefined,
  ignoreAddressValidation: boolean
) {
  if (!addresses) return [];
  const addressesFiltered = addresses
    .filter((address) => address.full !== null && address.full !== undefined)
    .map((address) => {
      const is_searchable: boolean =
        ignoreAddressValidation || addressHasPropertyReport(address);
      return {
        ...address,
        is_searchable,
      };
    });
  return sortAddressesBySeenDate(addressesFiltered);
}

function serializeEmails(emails: commonTypes.Emails | undefined) {
  if (!emails) return [];
  return emails.map((email) => {
    if (email.meta) {
      email.meta.is_derived = email.is_derived;
    }
    return {
      ...email,
    };
  });
}

function serializePhones(phones: commonTypes.Phones | undefined) {
  if (!phones) return [];
  const uniquePhones: { [key: string]: commonTypes.Phone } = {};

  phones.forEach((phone) => {
    if (phone.number) {
      const formattedPhone = formatCountryPhone(phone.number);

      if (!uniquePhones[formattedPhone]) {
        uniquePhones[formattedPhone] = phone;

        return {
          ...phone,
        };
      }
    }
  });

  return Object.values(uniquePhones);
}

function serializeSocialProfiles(profiles: peopleTypes.Profiles | undefined) {
  let serializedProfiles = sortProfiles(serializeProfiles(profiles || []));

  if (serializedProfiles) {
    const profileRegex = /www.linkedin.com\/in\/(.+)/i;
    const nicknames = serializedProfiles
      .filter((profile) => profile?.url?.match(profileRegex))
      .map((profile) => profile?.url?.match(profileRegex)?.[1]);

    if (nicknames) {
      const dupsExp = new RegExp(
        `//linkedin.com/in/(?:${nicknames.join('|')})`,
        'i'
      );

      serializedProfiles = serializedProfiles.filter(
        (profile) => !profile?.url?.match(dupsExp)
      );
    }
  }

  return serializedProfiles;
}

export function SerializeContact(
  rawPerson: peopleTypes.FullPerson,
  ignoreAddressValidation = false
) {
  const addresses = serializeAddresses(
    rawPerson.contact?.addresses,
    ignoreAddressValidation
  );

  const jobs = serializeJobs(rawPerson.jobs);
  const educations = rawPerson.educations.filter(
    (education: peopleTypes.Educations[number]) => education.institution
  );

  const emails = serializeEmails(rawPerson.contact?.emails);
  const phones = rawPerson?.contact?.phones;

  return {
    rawData: rawPerson,
    contact: {
      addresses: addresses || [],
      emails: emails || [],
      mainEmail: rawPerson.contact?.emails[0] || '',
      mainPhone: phones?.length ? phones[0] : null,
      phones: phones || [],
    },
    identity: {
      ...rawPerson.identity,
      name: rawPerson?.identity?.names[0].full,
    },
    jobs,
    rawJobs: rawPerson.jobs,
    educations,
    usernames: rawPerson.social.usernames,
    profiles: rawPerson.social?.profiles,
    userIds: rawPerson.social.user_ids,
    vehicles: { aircraft: [], automobiles: [], watercraft: [] },
    real_estate: rawPerson.real_estate,
    images: rawPerson.images,
    connections: {
      relatives: [],
      neighbors: [],
      associates: [],
    },
    obituary: undefined,
    bonusData: { licenses: rawPerson.licenses, filings: rawPerson.filings },
    marital: rawPerson.marital,
    courts: rawPerson.courts,
    upvoted: rawPerson.upvoted || false,
    downvoted: rawPerson.downvoted || false,
    hidden: rawPerson.hidden || false,
    facts: [],
  } as serializedTypes.SerializedPerson;
}

export function SerializeContactReport(rawPersonReport: reportTypes.Report) {
  let is_Blacklisted = false;
  const { entities } = rawPersonReport;
  const people = entities?.people?.filter(
    (person: peopleTypes.FullPerson) => person.identity
  );

  const serializedPeople = people?.map((person: peopleTypes.FullPerson) =>
    SerializeContact(person)
  );

  const mappedPeople = serializedPeople
    ? serializedPeople.map(mapPeople('entities.people'))
    : serializedPeople;

  if (serializedPeople) {
    serializedPeople[0].profiles = serializeSocialProfiles(
      serializedPeople[0].profiles
    );
    serializedPeople[0].contact.phones = serializePhones(
      serializedPeople[0].contact.phones
    );
  }

  if (
    rawPersonReport.meta?.report_info &&
    rawPersonReport.meta.report_info.proxy_flags
  ) {
    is_Blacklisted = isBlacklisted(
      rawPersonReport.meta.report_info.proxy_flags
    );
  }

  const serializedPersonReport = {
    rawData: rawPersonReport,
    people: mappedPeople,
    meta: rawPersonReport.meta,
    alert_me: rawPersonReport.meta?.alert_me || false,
    created_at: rawPersonReport.meta?.created_at || '',
    report_upgraded: rawPersonReport.meta?.report_upgraded || false,
    id: rawPersonReport.meta?.permalink || '',
    isBlacklisted: is_Blacklisted,
  } as serializedTypes.SerializedPersonReport;

  return serializedPersonReport;
}
